import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { organisationsApi, authApi, profileApi } from '../lib/api';

const newId = () => ((typeof crypto !== 'undefined' && crypto.randomUUID)
  ? crypto.randomUUID()
  : `p_${Date.now()}_${Math.random().toString(36).slice(2)}`);

/**
 * Everything the organisation page edits in place: homes, people, the
 * suggestions analysis found, short breaks, brothers and sisters, and naming
 * the council behind a holding organisation. The page owns the organisation
 * (one copy, loaded once); every write here goes to the server and then asks
 * the page to reload, rather than guessing the new shape locally.
 *
 * `reload()` must refetch GET /organisations/{id}/dashboard into the page.
 */
export const useOrgEditing = (org, reload) => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  // Kept on the account, not the organisation: short-break carers span
  // placements (and councils), brothers and sisters span everything.
  const [respitePool, setRespitePool] = useState([]);
  const [siblings, setSiblings] = useState([]);
  const [dob, setDob] = useState(null);
  const [allOrgs, setAllOrgs] = useState([]);

  const loadAccount = useCallback(async () => {
    try {
      const me = await authApi.getMe();
      setRespitePool(me?.data?.respite_carers || []);
      setSiblings(me?.data?.siblings || []);
      setDob(me?.data?.date_of_birth || null);
    } catch {
      // soft-fail — the lists catch up on the next visit
    }
  }, []);
  const loadAllOrgs = useCallback(() => {
    organisationsApi.getAll().then((r) => setAllOrgs(r.data?.organisations || [])).catch(() => setAllOrgs([]));
  }, []);
  useEffect(() => { loadAccount(); loadAllOrgs(); }, [loadAccount, loadAllOrgs, org?.id]);

  // The whole organisation through PUT, with only what the server needs.
  const persistOrg = useCallback(async (overrides) => {
    await organisationsApi.update(org.id, {
      type: org.type, name: org.name, location: org.location, address: org.address, email: org.email,
      website: org.website, start_date: org.start_date, end_date: org.end_date,
      placements: org.placements || [], social_workers: org.social_workers || [],
      year_group_start: org.year_group_start, year_group_end: org.year_group_end, notes: org.notes,
      reference_numbers: org.reference_numbers, parent_org_id: org.parent_org_id,
      parent_placement_id: org.parent_placement_id,
      ...overrides,
    });
    await reload();
  }, [org, reload]);

  // Run one write with the saving flag, a toast either way, and errors
  // reported in words.
  const run = useCallback(async (fn, { ok, fail }) => {
    setSaving(true);
    try {
      const out = await fn();
      if (ok) toast.success(ok);
      return out ?? true;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error((typeof detail === 'string' && detail) || fail);
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  const addPlacement = (placement) => run(
    () => persistOrg({ placements: [...(org.placements || []), { id: newId(), ...placement }] }),
    { ok: 'Home added.', fail: 'The home could not be added.' },
  );
  const removePlacement = (placementId) => run(
    () => persistOrg({ placements: (org.placements || []).filter((p) => p.id !== placementId) }),
    { ok: 'Home removed.', fail: 'It could not be removed.' },
  );
  const addPerson = (person) => run(
    () => persistOrg({ social_workers: [...(org.social_workers || []), person] }),
    { ok: 'Person added.', fail: 'The person could not be added.' },
  );
  const removePerson = (index) => run(
    () => persistOrg({ social_workers: (org.social_workers || []).filter((_, i) => i !== index) }),
    { ok: 'Person removed.', fail: 'It could not be removed.' },
  );

  // Sequential, never Promise.all, so two writes to one organisation never race.
  const confirmSuggestions = (ids, options = {}) => run(async () => {
    for (const sid of ids) await organisationsApi.confirmSuggestion(org.id, sid, options);
    await reload();
    // A brother, sister or short-break carer lands on the account.
    await loadAccount();
  }, { ok: 'Added.', fail: 'That suggestion could not be applied.' });
  const rejectSuggestions = (ids) => run(async () => {
    for (const sid of ids) await organisationsApi.rejectSuggestion(org.id, sid);
    await reload();
  }, { fail: 'That could not be dismissed.' });

  const removeSibling = (siblingId) => run(async () => {
    const next = siblings.filter((s) => s.id !== siblingId);
    await profileApi.update({ siblings: next });
    setSiblings(next);
  }, { fail: 'That could not be removed.' });

  const attachRespite = (placementId, { carerId, name, pattern }) => run(async () => {
    const res = await organisationsApi.attachRespite(org.id, placementId, {
      carer_id: carerId || null, new_carer: carerId ? null : { name }, pattern: pattern || null,
    });
    if (res.data?.respite_carers) setRespitePool(res.data.respite_carers);
    await reload();
  }, { ok: 'Short break recorded.', fail: 'That could not be saved.' });
  const detachRespite = (placementId, carerId) => run(async () => {
    await organisationsApi.detachRespite(org.id, placementId, carerId);
    await reload();
  }, { fail: 'That could not be removed.' });
  const promoteRespite = (carerId) => run(async () => {
    const res = await organisationsApi.promoteRespiteCarer(org.id, carerId);
    if (res.data?.respite_carers) setRespitePool(res.data.respite_carers);
    toast.success(`${res.data?.organisation?.name} added as its own organisation.`);
    navigate(`/organisations/${res.data.organisation.id}`);
  }, { fail: 'That could not be added.' });
  // Saving the pool itself (a carer added from the add-a-home dialog).
  const savePool = async (pool) => {
    setRespitePool(pool);
    try {
      await profileApi.update({ respite_carers: pool });
    } catch {
      toast.error('The short-break carer could not be saved.');
    }
  };

  // "Council not yet known" → a real council, by name or by merging into one
  // of the person's own (POST /organisations/{id}/resolve).
  const resolveCouncil = (body) => run(async () => {
    const res = await organisationsApi.resolve(org.id, body);
    if (res.data?.outcome === 'merged') {
      toast.success(`Placements moved to ${res.data.organisation?.name || 'that organisation'}.`);
      navigate(`/organisations/${res.data.organisation.id}`);
    } else {
      toast.success(`This is now ${res.data.organisation?.name}. You can send them a request.`);
      await reload();
    }
  }, { fail: 'That could not be saved.' });

  const orgNameById = Object.fromEntries(allOrgs.map((o) => [o.id, o.name]));

  return {
    saving, respitePool, siblings, dob, allOrgs, orgNameById,
    addPlacement, removePlacement, addPerson, removePerson,
    confirmSuggestions, rejectSuggestions, removeSibling,
    attachRespite, detachRespite, promoteRespite, savePool, resolveCouncil,
  };
};

export default useOrgEditing;
